import {
  AIComponent,
  AccessControl,
  ActionButtons,
  B1,
  CHAT_ACTION_TEXT,
  Editor,
  EventEmitter,
  Loader,
  P1,
  T2,
  TICKET_SOURCE,
  fetchApi,
  getAIRephraseKeys,
  getHeaderValue,
  getTranslatedText,
  toast,
  useActivityTracker
} from '@nile/components';
import { useGlobalChatHook } from '@nile/components/src/Components/GlobalChatHook';
import TimerActionsFromReply from '@nile/components/src/Containers/NewWorklog/TimerActionsFromReply';
import {
  dompurifier,
  formatContentWithMentionsAndPlaceholders,
  getCurrentUserHook,
  useUserMSPCheckHook
} from '@nile/components/src/Utils/helpers';
import {
  APPLY_AI_PROMPT,
  AccountClient,
  SAVE_TICKET_CONVERSATION,
  SAVE_TICKET_INTEGRATION_CONVERSATION,
  TicketClient
} from '@nile/graphql';
import { GET_CLIENT_USERS } from '@nile/graphql/src/Query/client';
import TicketNoteAIActions from '@nile/mangifera/src/Components/AI/TicketNoteAIActions';
import GlobalTicketConversationAction from '@nile/mangifera/src/Containers/ChatContainers/GlobalTicketConversationAction';
import GlobalTicketConversationUsers from '@nile/mangifera/src/Containers/ChatContainers/GlobalTicketConversationUsers';
import { SelectUsers } from '@nile/mangifera/src/Pebbles/ChatContainer';
import { useGlobalActionContainerStore } from '@nile/mangifera/src/StoreManager/store/actionContainerStore';
import { SVGIcon } from '@superops/super-icons';
import {
  Avatar,
  Badge,
  Button,
  ButtonGroup,
  Card,
  Checkbox,
  Popover,
  DisplayText
} from '@superops/ui-design-system';
import Tippy from '@tippyjs/react';
import dompurify from 'dompurify';
import { cloneDeep, isEmpty, isEqual, update } from 'lodash';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { Tooltip } from 'react-clone-tippy';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { FormattedMessage, useIntl } from 'react-intl';
import BannerComponent from '../../Components/BannerComponent.tsx';
import {
  CopyMailUsers,
  EditorHeader,
  EditorHolder,
  EmailStatus,
  MailboxItem,
  MailboxItemHolder,
  StyledChatContainer,
  ToolTipContainer,
  TypingStatus,
  Delete,
  Item
} from './ActionContainer';
import { HiddenActions, ToolbarActions } from './Helpers/helpers';
import SuperEditor from '@nile/components/src/Components/RichTextEditor/SuperEditor';
import { useSuperEditor } from '@nile/components/src/Components/RichTextEditor/useSuperEditor';

export const SuperopsAppReplyHeader = (props) => {
  const { label } = props;
  const name = `${label?.firstName} ${label?.lastName || ''}`;
  return (
    <div className="fx w100 astart">
      <div className="flex tw-gap-x-2 actr">
        <div>
          <Badge size="sm" status="critical" radial>
            <SVGIcon name="SuperopsLight" size="sm" />
          </Badge>
        </div>
        <div>
          <FormattedMessage id="im.superops.app" />
        </div>
        <div>
          <div className="flex center m4 ptr">
            <div className="tw-flex tw-gap-1">
              <Avatar name={name} initials={name?.[0]} size="extraSmall" />
              <div className="ml-5">{name}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ActionContainer = (props) => {
  // Destructure all props to avoid direct props references in useCallback hooks
  const {
    id: propsId,
    mode: propsMode,
    ticket,
    globalConversationFlag,
    recipients: recipientsFromProps,
    refetchDefaultReplyTemplate,
    uploadParams,
    attachmentUploadUrl,
    onCancel,
    allowMentions,
    enablePublic,
    splitOptions,
    notificationTemplateByConstant,
    editorDetails,
    checkHideGlobalTicketConversationCloseModal,
    checkIfActionToolbarDisabled,
    openTicketFromGlobalConversation,
    showAIRephrase,
    prompts,
    resetConversationMode,
    module,
    sendAgentCollisionDataFromFloatingEditor,
    actionButtonClassName,
    setInstance,
    parentRef,
    isDescOrder,
    resolvedConversationLoading,
    details,
    convertConversationToWorklog,
    project,
    isSplit,
    sendAIFeatureFeedback,
    concurrentUsersTyping,
    actionButtonStyles,
    hideRecipients,
    globalChatSuccessCallBack,
    imConfig
  } = props;

  const intl = useIntl();
  const timerRef = useRef(null);
  const editorRef = useRef(null);
  const editorContainerRef = useRef(null);
  const currentUser = getCurrentUserHook();
  const isMSP = useUserMSPCheckHook();

  const [localPrompts, setLocalPrompts] = useState(prompts || []);

  // Initialize store with ticket ID
  const mode = propsMode || 'conversation';
  const id = propsId || ticket?.ticketId || 'new_ticket';

  const useStore = useGlobalActionContainerStore({
    ticketId: ticket?.ticketId,
    componentId: `${mode}_${id}`,
    cacheConfig: {
      scope: 'global'
    }
  });

  // Get states from store

  const editorConfig = props?.editorConfig || {
    id: ticket?.ticketId,
    name: `${mode}_${id}`,
    cacheConfig: {
      scope: 'global'
    }
  };

  const { template, updateTemplate, resetTemplate } = useSuperEditor(
    editorConfig
  );

  const ccUsers = useStore((s) => s.ccUsers);
  const bccUsers = useStore((s) => s.bccUsers);
  const submitActionLoading = useStore((s) => s.submitActionLoading);
  const showCc = useStore((s) => s.showCc);
  const showBcc = useStore((s) => s.showBcc);
  const isUploading = useStore((s) => s.isUploading);
  const isDisabled = useStore((s) => s.isDisabled);
  const mailboxes = useStore((s) => s.mailboxes);
  const selectedMailbox = useStore((s) => s.selectedMailbox);
  const popoverActive = useStore((s) => s.popoverActive);
  const isPublicNote = useStore((s) => s.isPublicNote);
  const editorWidthIsSmall = useStore((s) => s.editorWidthIsSmall);
  const hideToolbar = useStore((s) => s.hideToolbar);
  const isBannerVisible = useStore((s) => s.isBannerVisible);

  // Get setter methods from store
  const initialize = useStore((s) => s.initialize);
  const initializeRecipients = useStore((s) => s.initializeRecipients);
  const setCcUsers = useStore((s) => s.setCcUsers);
  const setBccUsers = useStore((s) => s.setBccUsers);
  const setSubmitActionLoading = useStore((s) => s.setSubmitActionLoading);
  const setShowCc = useStore((s) => s.setShowCc);
  const setShowBcc = useStore((s) => s.setShowBcc);
  const setIsUploading = useStore((s) => s.setIsUploading);
  const setIsDisabled = useStore((s) => s.setIsDisabled);
  const setMailboxes = useStore((s) => s.setMailboxes);
  const setSelectedMailbox = useStore((s) => s.setSelectedMailbox);
  const setPopoverActive = useStore((s) => s.setPopoverActive);
  const setIsPublicNote = useStore((s) => s.setIsPublicNote);
  const setEditorWidthIsSmall = useStore((s) => s.setEditorWidthIsSmall);
  const setHideToolbar = useStore((s) => s.setHideToolbar);
  const setIsBannerVisible = useStore((s) => s.setIsBannerVisible);
  const recipients = useStore((s) => s.recipients);
  const setRecipients = useStore((s) => s.setRecipients);
  const isPrivateChannel = useStore((s) => s.isPrivateChannel);
  const setIsPrivateChannel = useStore((s) => s.setIsPrivateChannel);

  const { getGlobalConversations } = useGlobalChatHook();

  /**
   * Determines whether the save/submit button should be disabled based on various conditions
   *
   * @returns {boolean} - True if the button should be disabled, false otherwise
   */
  const getIsSaveButtonDisabled = () => {
    // Global conditions that always disable the button regardless of other factors
    if (isDisabled || isUploading) {
      return true;
    }

    // MSP-specific validation logic
    if (isMSP && module === 'TICKET_CONVERSATION') {
      // For standard tickets (non-Communication source)
      if (ticket?.source !== TICKET_SOURCE.Communication) {
        return (
          isEmpty(ticket?.requester) || !selectedMailbox?.outgoingDomainVerified
        );
      }

      // For integration tickets (TEAMS, Slack)
      if (isPrivateChannel) {
        return isEmpty(recipients);
      }
    }

    // Default case: button is enabled
    return false;
  };

  const isSaveButtonDisabled = getIsSaveButtonDisabled();

  const generateReceiverOptions = (users) => {
    const requester = ticket?.requester || null;
    const additionalRequesters = ticket?.additionalRequester || [];
    const currentUserId = currentUser?.userId;

    //  Filter users with roleType TECHNICIAN and not the current user
    const technicianUsers =
      users?.filter(
        (user) =>
          user?.user?.role?.roleType?.type === 'TECHNICIAN' &&
          user?.user?.userId !== currentUserId
      ) || [];
    // Get requester and additionalRequesters, filtering out the current user
    const otherRequesters = [requester, ...additionalRequesters]
      ?.filter((user) => user && user?.userId && user?.userId !== currentUserId)
      .map((user) => {
        return {
          user: user
        };
      });
    // Merge techs and requesters
    const allFilterUsers = [...technicianUsers, ...otherRequesters];
    // Deduplicate by userId
    const uniqueUsers = allFilterUsers?.filter(
      (user, index, self) =>
        index === self.findIndex((u) => u?.user?.userId === user?.user?.userId)
    );
    return uniqueUsers?.map((user) => {
      return {
        ...user,
        label: user?.user?.name,
        value: user?.user?.email
      };
    });
  };

  const customOptionComponent = (option) => {
    const { label } = option?.data;
    const { innerRef, innerProps } = option;
    return (
      <div ref={innerRef} {...innerProps} className="flex center p8 ptr">
        <div className="mr8">
          <Avatar initials={label?.[0]} size={'extraSmall'} />
        </div>
        {label}
      </div>
    );
  };

  const removeReceiver = (data) => {
    const updatedRecipients = recipients?.filter(
      (receiver) => receiver?.value !== data?.value
    );
    setRecipients(updatedRecipients);
  };

  const customMultiValueComponent = (option) => {
    const { data, innerRef, innerProps } = option;
    return (
      <div ref={innerRef} {...innerProps} className="flex center m4 ptr">
        <Item>
          <Avatar
            name={data?.label}
            initials={data?.label[0]}
            size="extraSmall"
          />
          <div className="ml-5">{data?.label}</div>
          {!data?.isFixed && (
            <Delete
              className="multi-text-delete"
              tabIndex="-1"
              role="button"
              onClick={() => removeReceiver(data)}
            >
              <Button
                plain
                icon={
                  <div className="fx center">
                    <SVGIcon name="Close" color="black" size="xs" />
                  </div>
                }
              />
            </Delete>
          )}
        </Item>
      </div>
    );
  };

  const recipientsConfig = useMemo(() => {
    return {
      property: 'recipientId',
      query: GET_CLIENT_USERS,
      params: {
        client: AccountClient,
        variables: { input: { clientId: ticket?.client?.accountId } }
      },
      methodName: 'getClientUserAssociationsList',
      generateOptions: generateReceiverOptions,
      customOptionComponent: customOptionComponent,
      customMultiValueComponent: customMultiValueComponent,
      isMulti: true,
      field: 'recipients',
      placeholder: getTranslatedText(intl, 'common.choose.technician'),
      errors: '',
      styles: {
        multiValueRemove: (base, state) => {
          if (state.data.isFixed) {
            return { ...base, display: 'none' };
          }
          return base;
        }
      },
      isClearable: false
    };
  }, [ticket, generateReceiverOptions, customMultiValueComponent, intl]);

  const getFormattedRecipients = (threads) => {
    if (!threads) {
      return [];
    }
    const formatRecipient = (receiver) => ({
      ...receiver,
      name: receiver?.user?.name,
      value: receiver?.user?.email,
      label: receiver?.user?.name, // showing name in the dropdown
      isFixed: true // default - unable to deselect from dropdown
    });

    const privateThreads = threads?.filter((thread) => {
      return thread?.conversationScope === 'PRIVATE';
    });

    const uniqueRecipients = privateThreads
      .flatMap((thread) => thread?.recipients?.receiver || [])
      .reduce((acc, item) => {
        const key = item?.user?.userId || item?.user?.email || item?.label;
        return { ...acc, [key]: item };
      }, {});

    return Object.values(uniqueRecipients).map(formatRecipient);
  };

  const getValueForDropdown = useCallback((user) => {
    return {
      ...user,
      name: user.name || user.email,
      value: user.name || user.email,
      label: user.name || user.email
    };
  }, []);

  const getFormattedCCUSers = useCallback((users) => {
    const allCCUsers = users.map((el) => {
      return getValueForDropdown(el);
    });
    return allCCUsers;
  }, []);

  const isResetTemplateRef = React.useRef(false);

  const updateState = useCallback((field, value) => {
    const setters = {
      ccUsers: setCcUsers,
      bccUsers: setBccUsers,
      showCc: setShowCc,
      showBcc: setShowBcc,
      isUploading: setIsUploading,
      isDisabled: setIsDisabled,
      mailboxes: setMailboxes,
      selectedMailbox: setSelectedMailbox,
      popoverActive: setPopoverActive,
      isPublicNote: setIsPublicNote,
      editorWidthIsSmall: setEditorWidthIsSmall,
      hideToolbar: setHideToolbar,
      isBannerVisible: setIsBannerVisible,
      disabled: setIsDisabled,
      submitActionLoading: setSubmitActionLoading
    };

    if (setters[field]) {
      setters[field](value);
    }
  }, []);

  const clearContent = useCallback(() => {
    resetTemplate();
    setCcUsers(
      recipientsFromProps?.cc ? getFormattedCCUSers(recipientsFromProps.cc) : []
    );
    setBccUsers(recipientsFromProps?.bcc || []);
    setSubmitActionLoading(false);
    setIsPublicNote(false);
    isResetTemplateRef.current = true;
    if (onCancel) {
      onCancel();
    }
  }, [recipientsFromProps, onCancel, getFormattedCCUSers]);

  const appendAdditionalRequestData = () => {
    let recipientDetails = [];
    if (ticket?.source === TICKET_SOURCE.Communication) {
      if (imConfig?.supportedChannelType === 'DM') {
        recipientDetails = [
          {
            user: {
              userId: ticket?.requester?.userId
            },
            referenceId: null,
            label: ticket?.requester?.email,
            name: ticket?.requester?.name,
            recipientType:
              ticket?.requester?.email === currentUser?.email
                ? 'SENDER'
                : 'RECEIVER'
          }
        ];
      }
      if (isPrivateChannel) {
        recipientDetails = recipients?.map((el) => {
          return {
            user: {
              userId: el?.user?.userId
            },
            referenceId: el?.referenceId || null,
            label: el?.user?.email, // send email as label
            name: el?.user?.name,
            recipientType: 'RECEIVER'
          };
        });
      }
      const senderPresentInReceipeint = recipientDetails?.some(
        (el) => (el?.user?.email || el?.label) === currentUser?.email
      );
      if (!senderPresentInReceipeint) {
        recipientDetails = [
          ...recipientDetails,
          {
            user: {
              userId: currentUser?.userId
            },
            referenceId: null,
            label: currentUser?.email,
            name: currentUser?.name,
            recipientType: 'SENDER'
          }
        ];
      }
      return {
        recipientDetails: recipientDetails,
        conversationScope:
          imConfig?.supportedChannelType === 'DM'
            ? 'APP'
            : isPrivateChannel === true
            ? 'PRIVATE'
            : 'PUBLIC'
      };
    }
    return {};
  };

  const onMessageSubmit = useCallback(
    (status) => {
      setSubmitActionLoading(true);
      const formattedContent = allowMentions
        ? formatContentWithMentionsAndPlaceholders(template?.content)
        : template?.content;
      let data = {
        message: dompurifier(formattedContent) || '',
        ccUsers: ccUsers || [],
        bccUsers: bccUsers || [],
        mentions: template?.mentions || [],
        attachments: template?.attachments || [],
        inlineImages: template?.inlineImages || [],
        type: status?.type || status?.name,
        selectedMailbox: selectedMailbox,
        suppressCloseNotification: status?.suppressCloseNotification
      };

      if (enablePublic) {
        data.privacyType = isPublicNote ? 'PUBLIC' : 'PRIVATE';
      }

      const appendRequestData = appendAdditionalRequestData();
      data = {
        ...data,
        ...appendRequestData
      };

      props?.onMessageSubmit(data);
      sessionStorage.removeItem('closeTicketClicked');
      sessionStorage.removeItem('suppressCloseNotificationClicked');
      if (status?.statusType === 'close') {
        sessionStorage.setItem('closeTicketClicked', true);
      }
    },
    [
      allowMentions,
      enablePublic,
      props?.onMessageSubmit,
      template,
      ccUsers,
      bccUsers,
      selectedMailbox,
      isPublicNote,
      appendAdditionalRequestData
    ]
  );

  const onMessageSubmitWithoutNotif = useCallback(
    (type) => {
      setSubmitActionLoading(true);
      const data = {
        message: dompurifier(template?.content) || '',
        ccUsers: ccUsers || [],
        bccUsers: bccUsers || [],
        mentions: template?.mentions || [],
        attachments: template?.attachments || [],
        inlineImages: template?.inlineImages || [],
        type: type,
        suppressCloseNotification: true,
        selectedMailbox: selectedMailbox
      };

      if (enablePublic) {
        data.privacyType = isPublicNote ? 'PUBLIC' : 'PRIVATE';
      }

      onMessageSubmit(data);
      sessionStorage.removeItem('closeTicketClicked');
      sessionStorage.setItem('suppressCloseNotificationClicked', true);
    },
    [
      enablePublic,
      onMessageSubmit,
      template,
      ccUsers,
      bccUsers,
      selectedMailbox,
      isPublicNote
    ]
  );

  const updateCopyUsers = useCallback((type, val) => {
    if (type === 'ccUsers') {
      setCcUsers(val);
    } else if (type === 'bccUsers') {
      setBccUsers(val);
    } else if (type === 'recipients') {
      setRecipients(val);
    }
  }, []);

  const togglePopoverActive = useCallback(() => {
    setPopoverActive(!popoverActive);
  }, [popoverActive]);

  const toggleChannel = useCallback(() => {
    setIsPrivateChannel(!isPrivateChannel);
  }, [isPrivateChannel]);

  const getSaveRestrictedError = useCallback(() => {
    if (isMSP) {
      if (isEmpty(ticket?.requester)) {
        return getTranslatedText(
          intl,
          'ticket.details.requestor.empty.errorMsg'
        );
      }
      if (!selectedMailbox?.outgoingDomainVerified) {
        return getTranslatedText(
          intl,
          'ticket.details.mailbox.email.unverified'
        );
      }
    }
    return null;
  }, [isMSP, ticket?.requester, selectedMailbox?.outgoingDomainVerified]);

  const updateNoteAsPublic = useCallback((value) => {
    setIsPublicNote(value);
  }, []);

  // For global conversation
  const formatData = useCallback((params) => {
    const ccUsersFormatted = [];
    const bccUsersFormatted = [];
    let recipientsData = [];
    const attachments = params?.template?.attachments
      ? params?.template?.attachments.map((el) => {
          const { fileName, originalFileName, fileSize } = el;
          return {
            fileName,
            originalFileName,
            fileSize
          };
        })
      : [];
    params?.ccUsers?.forEach((el) => {
      if (el?.email) {
        ccUsersFormatted.push({
          email: el?.email
        });
      } else if (el?.__isNew__) {
        ccUsersFormatted.push({
          email: el?.value
        });
      }
    });
    params?.bccUsers?.forEach((el) => {
      if (el?.email) {
        bccUsersFormatted.push({
          email: el?.email
        });
      } else if (el?.__isNew__) {
        bccUsersFormatted.push({
          email: el?.value
        });
      }
    });
    let variables = {
      ticketId: params?.data?.ticketId,
      content: params?.template?.content,
      attachments: attachments
    };

    // For private conversation - Instant Messaging
    if (params?.data?.source === TICKET_SOURCE.Communication) {
      if (params?.conversationScope === 'PRIVATE') {
        recipientsData = params?.recipients?.map((el) => {
          return {
            user: {
              userId: el?.user?.userId
            },
            referenceId: el?.referenceId || null,
            label: el?.user?.email, // send email as label
            name: el?.user?.name,
            recipientType: 'RECEIVER'
          };
        });
      }
      if (imConfig?.supportedChannelType === 'DM') {
        recipientsData = [
          {
            user: {
              userId: ticket?.requester?.userId
            },
            referenceId: null,
            label: ticket?.requester?.email,
            name: ticket?.requester?.name,
            recipientType: 'RECEIVER'
          }
        ];
      }
      const senderPresentInReceipeint = recipientsData?.some(
        (el) => (el?.user?.email || el?.label) === currentUser?.email
      );
      if (!senderPresentInReceipeint) {
        recipientsData = [
          ...recipientsData,
          {
            user: {
              userId: currentUser?.userId
            },
            referenceId: null,
            label: currentUser?.email,
            name: currentUser?.name,
            recipientType: 'SENDER'
          }
        ];
      }
    }

    if (params?.data?.source === TICKET_SOURCE.Communication) {
      variables = {
        ...variables,
        sendMail: true,
        conversationScope: params?.conversationScope,
        recipientDetails: recipientsData
      };
    } else {
      variables = {
        ...variables,
        toUsers: [
          {
            email: params?.toUsers?.email
          }
        ],
        ccUsers: ccUsersFormatted,
        bccUsers: bccUsersFormatted,
        fromMailbox: { boxId: params?.data?.selectedMailbox?.boxId },
        inlineImages: params?.template?.inlineImages
      };
    }

    return variables;
  }, []);

  const onTemplateChange = useCallback(() => {
    if (sendAgentCollisionDataFromFloatingEditor) {
      sendAgentCollisionDataFromFloatingEditor();
    }
  }, [sendAgentCollisionDataFromFloatingEditor]);

  const onGlobalConversationClose = (globalConversation, params) => {
    const isEventEmitter = params?.isEventEmitter;
    if (isEventEmitter) {
      resetTemplate();
    } else {
      updateTemplate(globalConversation?.template);
    }
    setCcUsers(globalConversation?.ccUsers);
    setBccUsers(globalConversation?.bccUsers);
    setRecipients(globalConversation?.recipients);
    setShowBcc(globalConversation?.bccUsers?.length > 0);
    setShowCc(globalConversation?.ccUsers?.length > 0);
    const updatedPrivateChannel =
      globalConversation?.conversationScope === 'PRIVATE';
    setIsPrivateChannel(updatedPrivateChannel);
    if (props?.onGlobalConversationClose) {
      props?.onGlobalConversationClose(globalConversation);
    }
  };

  const pinChat = () => {
    const globalConversations = getGlobalConversations();
    // This will give the previously present conversation;
    if (!isEmpty(globalConversations)) {
      const ticketDetailsConversation = globalConversations?.find(
        (el) => el?.module === 'TICKET'
      );
      if (ticketDetailsConversation?.data?.updateTemplate) {
        const updateTemplateFunction =
          ticketDetailsConversation?.data?.updateTemplate;
        const updatedTemplate = ticketDetailsConversation?.template;
        updateTemplateFunction(updatedTemplate);
      }
    }
    resetTemplate();
    const defaultTemplate = {
      name: '',
      content: dompurify.sanitize(props?.template) || '',
      placeholdersUsed: '',
      mentions: [],
      attachments: [],
      inlineImages: []
    };
    const usersConfig = {
      property: 'userId',
      query: GET_CLIENT_USERS,
      params: {
        client: AccountClient,
        variables: { input: { clientId: ticket?.client?.accountId } }
      },
      methodName: 'getClientUserAssociationsList'
    };
    const CHAT_PARAMS = {
      id: ticket?.ticketId,
      subject: ticket?.subject,
      module: 'TICKET',
      data: {
        ...ticket,
        selectedMailbox: selectedMailbox,
        splitOptions: splitOptions,
        notificationTemplateByConstant: notificationTemplateByConstant,
        updateTemplate: updateTemplate
      },
      ccUsers: ccUsers,
      bccUsers: bccUsers,
      toUsers: ticket?.requester,
      recipients: recipients,
      conversationScope: isPrivateChannel === true ? 'PRIVATE' : 'PUBLIC',
      recipientsConfig: { ...recipientsConfig },
      toUsersConfig: usersConfig,
      ccUsersConfig: { ...usersConfig, isMulti: true, creatable: true },
      bccUsersConfig: { ...usersConfig, isMulti: true },
      formatData: formatData,
      template: template,
      editorDetails: editorDetails || {},
      mutationClient: TicketClient,
      mutationAPI:
        ticket?.source === TICKET_SOURCE.Communication
          ? SAVE_TICKET_INTEGRATION_CONVERSATION
          : SAVE_TICKET_CONVERSATION,
      callBack: globalChatSuccessCallBack,
      defaultTemplate: defaultTemplate,
      onTemplateChange: onTemplateChange,
      attachmentUploadParams: uploadParams,
      attachmentUploadUrl: attachmentUploadUrl,
      checkHideCloseModal: checkHideGlobalTicketConversationCloseModal,
      onOpen: checkIfActionToolbarDisabled,
      onTitleClick: openTicketFromGlobalConversation,
      primaryAction: GlobalTicketConversationAction,
      onClose: onGlobalConversationClose,
      toolbarContent: GlobalTicketConversationUsers,
      showWorklog: true,
      workItem: ticket,
      showAIRephrase: showAIRephrase,
      aiPrompts: localPrompts,
      imConfig: imConfig
    };
    EventEmitter.dispatch('createGlobalConversation', CHAT_PARAMS);
    useActivityTracker({
      event: 'Conversation was popped out'
    });
    if (resetConversationMode) {
      resetConversationMode(false);
    }
  };

  const toggleEditorToolbar = useCallback(() => {
    const editor = editorRef.current?.FroalaEditorRef?.current;
    // While adding link it won't be appended properly and gets added as new text
    // Reached out to Froala team and added editor?.events?.focus()
    // This ensures that when the toolbar is toggled, the editor also gains focus, allowing the toolbar to behave as expected.
    editor?.events?.focus();
    setHideToolbar(!hideToolbar);
  }, [editorRef, hideToolbar, setHideToolbar]);

  const handleImageUploading = (val) => {
    setIsDisabled(val);
  };

  const handlePageResize = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      setEditorWidthIsSmall(
        editorContainerRef?.current?.getBoundingClientRect()?.width < 710
      );
    }, 1000);
  }, []);

  const onCopy = useCallback(async (content) => {
    EventEmitter.dispatch('insertEditorContent', dompurifier(content));
  }, []);
  let copyTimeout = null;

  const onArticleCopy = async (content) => {
    await EventEmitter.dispatch(
      'insertOnlyEditorContent',
      dompurifier(content)
    );
    if (copyTimeout) {
      clearTimeout(copyTimeout);
    }
    copyTimeout = setTimeout(() => {
      const updatedContent = editorRef.current.FroalaEditorRef?.current?.html?.get();
      updateTemplate(updatedContent);
    }, 120);
  };

  const onRephraseCopy = useCallback((content, params) => {
    if (editorRef.current?.FroalaEditorRef) {
      EventEmitter.dispatch('insertEditorContent', dompurifier(content));
    } else {
      EventEmitter.dispatch('replaceEditorContent', dompurifier(content));
    }
  }, []);

  const actionButtonClassNames = [
    'fx end jend save-card z1',
    actionButtonClassName
  ]
    .filter(Boolean)
    .join(' ');

  const onAIRephraseFeedback = useCallback(
    (params) => {
      const featureId = params?.data?.currentPrompt?.featureId;
      const actualMessage = params?.data?.selectedText;
      const thumbsUp = params?.type === 'HELPFUL';
      const response = params?.response;

      if (featureId && actualMessage && response && sendAIFeatureFeedback) {
        const input = {
          feature: {
            featureId: featureId
          },
          ratingType: 'THUMBS',
          rating: thumbsUp ? '1' : '0', // 0 for down thumps
          prompt: {
            actualMessage: actualMessage,
            aiMessage: response
          }
        };

        sendAIFeatureFeedback({
          variables: {
            input: input
          }
        });
      }
    },
    [sendAIFeatureFeedback]
  );

  const aiRephraseKeys = useMemo(() => getAIRephraseKeys(intl), [intl]);

  const activator = (
    <div className="pointer fx actr" onClick={togglePopoverActive}>
      <div className="flex center">
        <T2 className="ml8">
          {selectedMailbox?.outgoingAddress ? (
            <>{`${selectedMailbox?.outgoingAddress}`}</>
          ) : (
            <>
              <FormattedMessage id="ticket.conversation.mailbox.select.placeholder" />
            </>
          )}
        </T2>
        <div className="ml8 flex center">
          <SVGIcon name="ArrowDown" />
        </div>
      </div>
    </div>
  );

  // useEffect for setting instance and parentRef - separate from componentDidMount to avoid dependency issues
  useEffect(() => {
    const currentState = {
      template,
      ccUsers,
      bccUsers,
      isDisabled,
      selectedMailbox,
      isPublicNote
    };

    const instanceObject = {
      updateTemplate,
      clearContent,
      onMessageSubmit,
      updateState,
      state: currentState
    };

    if (setInstance) {
      setInstance(instanceObject);
    }

    if (parentRef) {
      parentRef.current = instanceObject;
    }
  }, [
    template,
    ccUsers,
    bccUsers,
    isDisabled,
    selectedMailbox,
    isPublicNote,
    updateTemplate,
    clearContent,
    onMessageSubmit,
    updateState,
    setInstance,
    parentRef
  ]);

  const onTemplateSuccess = (content, attachments) => {
    EventEmitter.dispatch('insertEditorContent', content);
    const currentAttachments = template?.attachments || [];
    if (attachments?.length > 0) {
      const updatedAttachments = [...currentAttachments, ...attachments];
      updateTemplate({
        attachments: updatedAttachments
      });
    }
  };

  const onMailboxSuccess = (content, attachments) => {
    updateTemplate({
      content: content,
      attachments: attachments
    });
  };

  const updateTemplateContent = (newTemplate) => {
    if (ticket?.ticketId && selectedMailbox?.boxId) {
      const input = {
        input: {
          ticket: {
            ticketId: ticket.ticketId
          },
          mailbox: {
            boxId: selectedMailbox.boxId
          },
          template: {
            templateId: newTemplate.id
          }
        }
      };

      refetchDefaultReplyTemplate(input, onTemplateSuccess);
    }
  };

  const updateTemplateData = (newTemplate) => {
    if (newTemplate?.content) {
      const content = dompurifier(newTemplate?.content || '');
      const attachments = newTemplate.attachments || newTemplate.attachmentList;
      EventEmitter.dispatch('insertEditorContent', newTemplate);
      updateTemplate({
        content,
        attachments
      });
    }
  };

  const onMailboxChange = (mailbox) => {
    setSelectedMailbox(mailbox);
    togglePopoverActive();
    const input = {
      input: {
        ticket: {
          ticketId: ticket.ticketId
        },
        mailbox: {
          boxId: mailbox.boxId
        }
      }
    };
    refetchDefaultReplyTemplate(input, onMailboxSuccess);
  };

  const initializeRecipientsData = () => {
    const formattedCCUsers = getFormattedCCUSers(recipientsFromProps?.cc || []);
    const formattedBCCUsers = getFormattedCCUSers(
      recipientsFromProps?.bcc || []
    );
    const formattedRecipients = getFormattedRecipients(
      recipientsFromProps?.conversationThreads || []
    );
    initializeRecipients({
      ccUsers: formattedCCUsers,
      bccUsers: formattedBCCUsers,
      showCc: formattedCCUsers?.length > 0,
      showBcc: formattedBCCUsers?.length > 0,
      selectedMailbox: recipientsFromProps?.selectedMailbox,
      mailboxes: recipientsFromProps?.mailboxes || [],
      recipients: formattedRecipients
    });
  };

  useEffect(() => {
    if (recipientsFromProps) {
      initializeRecipientsData();
    }
  }, [recipientsFromProps]);

  const handleAIFeaturesUpdated = useCallback(
    (payload) => {
      const updatedPrompts =
        payload?.promptBuilderSubFeatures ||
        payload?.features?.find((feat) => feat?.constant === 'PROMPT_BUILDER')
          ?.subFeatures ||
        [];

      if (!isEqual(localPrompts, updatedPrompts)) {
        setLocalPrompts(updatedPrompts);
      }
    },
    [localPrompts]
  );

  useEffect(() => {
    if (prompts && !isEqual(localPrompts, prompts)) {
      setLocalPrompts(prompts);
    }
  }, [prompts]);

  // Initial useEffect
  useEffect(() => {
    initialize({
      hideToolbar: props.hideToolbar,
      isPrivateChannel: props?.isPrivateChannel
    });

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      setEditorWidthIsSmall(
        editorContainerRef?.current?.getBoundingClientRect()?.width < 710
      );
    }, 1000);

    EventEmitter.subscribe('imageUploading', handleImageUploading);
    EventEmitter.subscribe('aiFeaturesUpdated', handleAIFeaturesUpdated);

    handlePageResize();
    window.addEventListener('resize', handlePageResize);

    return () => {
      EventEmitter.unsubscribe('imageUploading');
      EventEmitter.unsubscribe('aiFeaturesUpdated');
      window.removeEventListener('resize', handlePageResize);
    };
  }, []);

  return (
    <StyledChatContainer
      ref={editorContainerRef}
      color={
        module === 'TICKET_NOTE' || module === 'NOTE' ? '#fffae4' : '#ffffff'
      }
      isDescOrder={isDescOrder}
      className="position-relative height450 max-height-450"
    >
      <Card AdaptHeight padding="sizeSmall">
        <div className="fhp position-relative overflow-y-auto x-hidden">
          {enablePublic && (
            <div className="fx fw mb-10">
              <P1 className="fx actr pl10">
                <B1>
                  <FormattedMessage id="invoice.details.notes" />
                </B1>
              </P1>
              <div className="flex actr g10 mlauto">
                {showAIRephrase && (
                  <TicketNoteAIActions
                    editorRef={editorRef}
                    onCopy={onRephraseCopy}
                    ticket={ticket}
                    type="REPHRASE"
                    hideButtonText
                    prompts={localPrompts || []}
                    data={{
                      Api: APPLY_AI_PROMPT,
                      accessorKey: 'applyAIPrompt'
                    }}
                    sendFeedback={onAIRephraseFeedback}
                    featureName="PROMPT_BUILDER"
                    headingText={getTranslatedText(
                      intl,
                      'common.rephrase.text'
                    )}
                    headingIcon={'AIWand'}
                    loadingText={aiRephraseKeys?.loadingText}
                    successText={aiRephraseKeys?.successText}
                    copyButtonText={getTranslatedText(
                      intl,
                      'common.insert.text'
                    )}
                    copySuccessText={getTranslatedText(intl, 'common.inserted')}
                    mixpanelEventOnClick={{
                      event: 'Rephrased with prompt - Ticket note reply',
                      data: {
                        ticketId: ticket?.ticketId
                      }
                    }}
                  />
                )}
                {ticket && !showAIRephrase && (
                  <AIComponent
                    //  hideCopyPaste
                    onCopy={onCopy}
                    featureName="TICKET_CONVERSATION_SUMMARIZE"
                    workItem={{
                      itemId: ticket?.ticketId,
                      module: 'TICKET'
                    }}
                    summaryCase="NOTE"
                    summaryType="POINTS"
                    variables={{
                      input: {
                        itemId: ticket?.ticketId,
                        itemType: 'TICKET',
                        summaryCase: 'NOTE',
                        summaryType: 'POINTS'
                      }
                    }}
                    headingText={getTranslatedText(
                      intl,
                      'msp.ai.ticket.note.summary'
                    )}
                    headingIcon={'AI_TICKET'}
                    copyButtonText={getTranslatedText(
                      intl,
                      'common.insert.text'
                    )}
                    copySuccessText={getTranslatedText(intl, 'common.inserted')}
                    loadingText={[
                      getTranslatedText(
                        intl,
                        'msp.ai.ticket.note.summary.loading.message.1'
                      ),
                      getTranslatedText(
                        intl,
                        'msp.ai.ticket.note.summary.loading.message.2'
                      ),
                      getTranslatedText(
                        intl,
                        'msp.ai.ticket.note.summary.loading.message.3'
                      ),
                      getTranslatedText(
                        intl,
                        'msp.ai.ticket.note.summary.loading.message.4'
                      ),
                      getTranslatedText(
                        intl,
                        'msp.ai.ticket.note.summary.loading.message.5'
                      ),
                      getTranslatedText(
                        intl,
                        'msp.ai.ticket.note.summary.loading.message.6'
                      ),
                      getTranslatedText(
                        intl,
                        'msp.ai.ticket.note.summary.loading.message.7'
                      )
                    ]}
                    successText={[
                      getTranslatedText(
                        intl,
                        'msp.ai.ticket.summary.success.message.1'
                      ),
                      getTranslatedText(
                        intl,
                        'msp.ai.ticket.summary.success.message.2'
                      ),
                      getTranslatedText(
                        intl,
                        'msp.ai.ticket.summary.success.message.3'
                      ),
                      getTranslatedText(
                        intl,
                        'msp.ai.ticket.summary.success.message.4'
                      )
                    ]}
                    type="SUMMARY"
                    mixpanelEventOnClick={{
                      event: 'Clicked on AI note summary',
                      data: {
                        ticketId: ticket?.ticketId
                      }
                    }}
                  />
                )}

                <Checkbox
                  checked={isPublicNote}
                  onChange={updateNoteAsPublic}
                  label={getTranslatedText(
                    intl,
                    'workItem.details.note.mark.as.public'
                  )}
                />
              </div>
            </div>
          )}

          {!hideRecipients &&
            isMSP &&
            ticket?.source !== TICKET_SOURCE.Communication && (
              <>
                <EditorHeader className="flex mb4">
                  <div>
                    <div className="flex center mb10">
                      <div>
                        <FormattedMessage id="ticket.details.chat.from" />
                      </div>

                      <Popover
                        active={popoverActive}
                        activator={activator}
                        preferredPosition="below"
                        preferredAlignment="left"
                        onClose={togglePopoverActive}
                      >
                        <MailboxItemHolder>
                          {mailboxes?.map((el) => {
                            return (
                              <div className="fx actr" key={el.boxId}>
                                <MailboxItem
                                  onClick={() => onMailboxChange(el)}
                                  isVerified={el?.outgoingDomainVerified}
                                >
                                  {el?.outgoingAddress}
                                </MailboxItem>
                                {!el?.outgoingDomainVerified && (
                                  <Tippy
                                    content={
                                      <ToolTipContainer className="fx">
                                        <FormattedMessage id="ticket.details.mailbox.email.unverified" />
                                      </ToolTipContainer>
                                    }
                                    duration={250}
                                    interactive
                                    placement="right"
                                    arrow={false}
                                    allowHTML
                                    appendTo={document.body}
                                  >
                                    <EmailStatus className="fx actr">
                                      <SVGIcon
                                        name="InlineError"
                                        color="#ff0055"
                                        size="sm"
                                      />
                                    </EmailStatus>
                                  </Tippy>
                                )}
                              </div>
                            );
                          })}
                        </MailboxItemHolder>
                      </Popover>

                      {(isEmpty(selectedMailbox) ||
                        !selectedMailbox?.outgoingDomainVerified) && (
                        <Tippy
                          content={
                            <ToolTipContainer>
                              {isEmpty(selectedMailbox) ? (
                                <FormattedMessage id="ticket.details.mailbox.select.error.message" />
                              ) : (
                                <FormattedMessage id="ticket.details.mailbox.email.unverified" />
                              )}
                            </ToolTipContainer>
                          }
                          duration={250}
                          interactive
                          placement="right"
                          arrow={false}
                          allowHTML
                          appendTo={document.body}
                        >
                          <EmailStatus className="fx actr">
                            <SVGIcon
                              name="InlineError"
                              color="#ff0055"
                              size="sm"
                            />
                          </EmailStatus>
                        </Tippy>
                      )}
                    </div>
                    {ticket?.requester && (
                      <div className="flex center">
                        <div className="uppercase">
                          <FormattedMessage id="ticket.details.chat.to" />
                        </div>
                        <T2 className="ml8">
                          {`${ticket?.requester?.firstName} ${ticket?.requester
                            ?.lastName || ''}`}
                        </T2>
                        <T2 className="ml8 f-12">
                          ({ticket?.requester?.email})
                        </T2>
                      </div>
                    )}
                  </div>
                  {globalConversationFlag ? (
                    <div className="mlauto">
                      <ToolbarActions
                        pinChat={pinChat}
                        disabled={
                          isEmpty(selectedMailbox) ||
                          !selectedMailbox?.outgoingDomainVerified
                        }
                        updateTemplateContent={updateTemplateContent}
                        updateState={updateState}
                        showAIRephrase={showAIRephrase}
                        editorRef={editorRef}
                        onAIRephraseCopy={onRephraseCopy}
                        onArticleCopy={onArticleCopy}
                        prompts={localPrompts || []}
                        sendFeedback={onAIRephraseFeedback}
                        ticketId={ticket?.ticketId}
                        source={ticket?.source}
                      />
                    </div>
                  ) : (
                    <HiddenActions
                      updateTemplateContent={updateTemplateData}
                      updateState={updateState}
                      globalConversationFlag={globalConversationFlag}
                    />
                  )}
                </EditorHeader>
                <div>
                  {showCc && (
                    <CopyMailUsers>
                      <div className="title">CC</div>
                      <SelectUsers
                        updateField={updateCopyUsers.bind(null, 'ccUsers')}
                        property="userId"
                        query={GET_CLIENT_USERS}
                        params={{
                          client: AccountClient,
                          variables: {
                            input: { clientId: ticket?.client?.accountId }
                          }
                        }}
                        methodName="getClientUserAssociationsList"
                        field="cc"
                        value={ccUsers}
                        isMulti
                        creatable
                      />
                    </CopyMailUsers>
                  )}
                  {showBcc && (
                    <CopyMailUsers>
                      <div className="title">BCC</div>
                      <SelectUsers
                        updateField={updateCopyUsers.bind(null, 'bccUsers')}
                        property="userId"
                        methodName="getClientUserAssociationsList"
                        query={GET_CLIENT_USERS}
                        params={{
                          client: AccountClient,
                          variables: {
                            input: { clientId: ticket?.client?.accountId }
                          }
                        }}
                        field="bcc"
                        value={bccUsers}
                        isMulti
                        creatable
                      />
                    </CopyMailUsers>
                  )}
                </div>
              </>
            )}

          {isMSP &&
            ticket?.source === TICKET_SOURCE.Communication &&
            (imConfig?.supportedChannelType === 'DM' ? (
              <EditorHeader className="flex mb4">
                <SuperopsAppReplyHeader label={ticket?.requester} />
                <div className="mlauto">
                  <ToolbarActions
                    pinChat={pinChat}
                    updateTemplateContent={updateTemplateData}
                    updateState={updateState}
                    showAIRephrase={showAIRephrase}
                    editorRef={editorRef}
                    onAIRephraseCopy={onRephraseCopy}
                    onArticleCopy={onArticleCopy}
                    prompts={localPrompts || []}
                    sendFeedback={onAIRephraseFeedback}
                    ticketId={ticket?.ticketId}
                    source={ticket?.source}
                  />
                </div>
              </EditorHeader>
            ) : (
              <EditorHeader className="flex mb4">
                <div className="fx w100 astart">
                  <div className="fx g5 pt8 pointer" onClick={toggleChannel}>
                    <div
                      className={`flex br8 p4 fhp ${
                        !isPrivateChannel ? 'bg-green' : 'bg-grey'
                      }`}
                    >
                      <SVGIcon
                        name={
                          !isPrivateChannel
                            ? 'GlobeSharedView'
                            : 'PrivateArticlePlain'
                        }
                        size="sm"
                      />
                    </div>
                    <FormattedMessage
                      id={
                        !isPrivateChannel
                          ? 'common.public.channel'
                          : 'common.private.channel'
                      }
                    />

                    <div className="flex center">
                      <SVGIcon name="ArrowDown" />
                    </div>
                  </div>

                  {isPrivateChannel && (
                    <div className="w75">
                      <SelectUsers
                        updateField={(val) =>
                          updateCopyUsers('recipients', val)
                        }
                        {...recipientsConfig}
                        value={recipients}
                      />
                    </div>
                  )}
                </div>

                <div className="mlauto">
                  <ToolbarActions
                    pinChat={pinChat}
                    updateTemplateContent={updateTemplateData}
                    updateState={updateState}
                    showAIRephrase={showAIRephrase}
                    editorRef={editorRef}
                    onAIRephraseCopy={onRephraseCopy}
                    onArticleCopy={onArticleCopy}
                    prompts={localPrompts || []}
                    sendFeedback={onAIRephraseFeedback}
                    ticketId={ticket?.ticketId}
                    source={ticket?.source}
                  />
                </div>
              </EditorHeader>
            ))}

          <EditorHolder
            className="editorHolder"
            isBannerVisible={isBannerVisible}
          >
            {resolvedConversationLoading && (
              <div className="mt20">
                <Loader />
              </div>
            )}
            <SuperEditor
              focusEditor
              module={module || 'TICKET'}
              details={details}
              title="Add Attachments"
              description="Upload/drop files here"
              allowMultiple
              toolbarSticky={false}
              config={{
                htmlUntouched: true
              }}
              allowMentions={allowMentions}
              storeConfig={editorConfig}
              uploadParams={uploadParams}
              attachmentUploadUrl={attachmentUploadUrl}
              editorRef={editorRef}
              hideToolbar={hideToolbar}
            />
          </EditorHolder>

          <BannerComponent
            bannerKey="ticketReplyBanner"
            defaultMessage={getTranslatedText(
              intl,
              'ticket.reply.convert.worklog.info'
            )}
            onBannerChange={(bool) => {
              setIsBannerVisible(bool);
            }}
          />

          <div className="fx actr">
            <div className="fx actr fg1">
              {concurrentUsersTyping?.length ? (
                <TypingStatus className="fx actr mr-10">
                  <SVGIcon name="Edit" color="#4B8413" />
                  <FormattedMessage
                    id={
                      concurrentUsersTyping?.length > 2
                        ? 'common.collaborator.several.typing'
                        : 'common.collaborator.isTyping'
                    }
                    values={{
                      collabUserName: concurrentUsersTyping
                        ?.slice(0, 2)
                        ?.map((viewer) => viewer?.user?.name)
                        .join(', ')
                    }}
                  />
                </TypingStatus>
              ) : null}
              {props.hideToolbar !== false && (
                <span className="mr-10">
                  <Button
                    className="toggleFroalaToolbarMenu mr"
                    onClick={toggleEditorToolbar}
                  >
                    A
                  </Button>
                </span>
              )}

              {convertConversationToWorklog && (
                <AccessControl
                  roleToCheck={{
                    roles: ['tickets.edit', 'serviceCatalog.view']
                  }}
                  render={
                    <div className="fx actr fg1">
                      <TimerActionsFromReply
                        froalaEditor
                        bannerKey="ticketReplyBanner"
                        editorRef={editorRef}
                        workItem={ticket || project}
                      />
                    </div>
                  }
                />
              )}
            </div>
            <ActionButtons
              className={actionButtonClassNames}
              id="actionsContinerActionsHolder"
              position={actionButtonStyles?.position}
              right={actionButtonStyles?.right}
              bottom={actionButtonStyles?.bottom}
            >
              <ButtonGroup className="fx">
                <Button
                  plainSecondary
                  onClick={clearContent}
                  customClassName="actionsCancelBtn"
                >
                  <FormattedMessage id="common.cancel" />
                </Button>
                {isSplit ? (
                  <Tooltip
                    distance={15}
                    key={getSaveRestrictedError()}
                    title={getSaveRestrictedError()}
                    position="top-start"
                    inertia
                    trigger="mouseenter"
                    animation="shift"
                    animateFill={false}
                    disabled={!isSaveButtonDisabled}
                  >
                    <Button
                      loading={submitActionLoading}
                      onClick={() => onMessageSubmit()}
                      primary
                      disabled={isSaveButtonDisabled}
                      connectedDisclosure={{
                        disabled: isSaveButtonDisabled,
                        actions: splitOptions.map((el) => {
                          return {
                            content: (
                              <div className="fx actr">
                                <div
                                  className="fg1"
                                  onClick={onMessageSubmit.bind(null, el)}
                                >
                                  {el.content}
                                </div>

                                {notificationTemplateByConstant?.activated &&
                                  el.statusType === 'close' && (
                                    <span
                                      onClick={onMessageSubmitWithoutNotif.bind(
                                        null,
                                        el.name
                                      )}
                                      className="fx actr mlauto ptr"
                                    >
                                      <Tooltip
                                        html={
                                          <div className="p3 f12">
                                            {getTranslatedText(
                                              intl,
                                              'ticket.action.closeWithoutNotification'
                                            )}
                                          </div>
                                        }
                                        delay={400}
                                        duration={250}
                                        position="bottom"
                                        distance={10}
                                        inertia
                                        trigger="mouseenter"
                                        animation="shift"
                                        animateFill={false}
                                      >
                                        <Badge
                                          radial
                                          status="attention"
                                          icon={
                                            <SVGIcon
                                              name="NotificationsBellCrossed"
                                              size="sm"
                                            />
                                          }
                                        />
                                      </Tooltip>
                                    </span>
                                  )}
                              </div>
                            )
                          };
                        })
                      }}
                      icon={
                        !submitActionLoading && (
                          <SVGIcon name="SendWhite" color="#fff" />
                        )
                      }
                    >
                      <FormattedMessage id="ticket.details.chat.submit" />
                    </Button>
                  </Tooltip>
                ) : (
                  <Button
                    loading={submitActionLoading}
                    primary
                    disabled={isSaveButtonDisabled}
                    onClick={() => onMessageSubmit()}
                    customClassName="actionsSaveBtn"
                    icon={
                      !submitActionLoading && (
                        <SVGIcon name="SendWhite" color="#fff" />
                      )
                    }
                  >
                    <FormattedMessage
                      id={
                        CHAT_ACTION_TEXT[module] || 'ticket.details.chat.submit'
                      }
                    />
                  </Button>
                )}
              </ButtonGroup>
            </ActionButtons>
          </div>
        </div>
      </Card>
    </StyledChatContainer>
  );
};

export default ActionContainer;
